import React from 'react';
import moment from 'moment';
import i18next from 'i18next';
import { Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@thinknet-gmbh/platform-common-ui/';
import { PERMISSION_ACTIONS } from '@thinknet-gmbh/platform-common';
import { useGoogleFonts, GoogleFontsStatus } from '@flayyer/use-googlefonts';

import { ApolloProvider } from 'src/providers/';
import { useSessionContext } from 'src/contexts';
import MemberContextProvider from 'src/providers/member.provider';
import CompanyContextProvider from 'src/providers/company.provider';
import SubscriptionProvider from 'src/providers/subscription.provider';
import PermissionContextProvider from 'src/providers/permission.provider';
import LayoutWrapper from 'src/components/app/layout-wrapper/layout-wrapper.component';

import { ROUTES, ROUTES_RELATIONS } from './app-routes.constants';
import { ProtectedRoute, ProtectedRouteProps } from './protected-route';

moment.updateLocale(i18next.language, { week: { dow: 1 } });

export const App: React.FC = () => {
  const [sessionContext, updateSessionContext] = useSessionContext();

  const setRedirectPath = (path: string) => {
    updateSessionContext({ ...sessionContext, redirectPath: path });
  };

  const defaultProtectedRouteProps: ProtectedRouteProps = {
    isAuthenticated: sessionContext.isAuthenticated,
    authenticationPath: ROUTES.auth,
    redirectPath: sessionContext.redirectPath,
    setRedirectPath: setRedirectPath,
    action: PERMISSION_ACTIONS.read,
  };

  const { status } = useGoogleFonts([{ family: 'Inter', styles: ['400..700'] }]);

  const defaultPageProps = {
    fontIsLoaded: status === GoogleFontsStatus.LOADED,
  };

  return (
    <ApolloProvider>
      <PermissionContextProvider>
        <CompanyContextProvider>
          <MemberContextProvider>
            <ThemeProvider>
              <Routes>
                {ROUTES_RELATIONS.map(({ path, PageComponent, isProtected = true, children }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      isProtected ? (
                        <ProtectedRoute {...defaultProtectedRouteProps}>
                          <LayoutWrapper isProtected={isProtected}>
                            <SubscriptionProvider>
                              <PageComponent {...defaultPageProps} />
                            </SubscriptionProvider>
                          </LayoutWrapper>
                        </ProtectedRoute>
                      ) : (
                        <PageComponent {...defaultPageProps} />
                      )
                    }
                  >
                    {children?.map(({ subpath, SubPageComponent }) => (
                      <Route
                        key={subpath}
                        path={subpath}
                        element={
                          <LayoutWrapper>
                            <SubPageComponent />
                          </LayoutWrapper>
                        }
                      />
                    ))}
                  </Route>
                ))}
              </Routes>
            </ThemeProvider>
          </MemberContextProvider>
        </CompanyContextProvider>
      </PermissionContextProvider>
    </ApolloProvider>
  );
};

export default App;
